import { useEffect, useState } from "react";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import PageHeader from "../../components/PageHeader";
import { popupNavigation } from "../../util/functions/UniversalFunctions";
import { useNavigate } from "react-router-dom";
import { MessagePayload } from "../../types/messageType";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { useBranchCode } from "../../util/hooks/useBranch";
import { fetchStaff } from "../../features/staff/staffSlice";
import { getStudents } from "../../features/student/StudentSlice";
import messagesApis from "../../api/messagesApis";
import { toast } from "sonner";
import DocumentUpload from "../../components/common/DocumetUpload";

export default function SendMessage() {
  const branchCode = useBranchCode();
  const [formData, setFormData] = useState<MessagePayload>({
    message: "",
    staffId: "",
    studentId: "",
    attachmentUrl: "",
    branchCode,
  });

  const dispatch = useDispatch<AppDispatch>();
  const { staffList } = useSelector((state: RootState) => state.staff);
  const { students } = useSelector((state: RootState) => state.student);

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      getStudents({
        branchCode,
        page: 0,
        size: 100,
      }),
    );
  }, [branchCode, dispatch]);

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        branchCode,
        page: 0,
        size: 100,
        fullName: "",
        mobile: "",
      }),
    );
  }, [branchCode, dispatch]);

  const navigate = useNavigate();

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const studentOptions = students.map((student) => ({
    label: `${student.fullName}`,
    value: `${student.admissionNumber}`,
  }));

  const staffOptions = staffList.map((staff) => ({
    label: `${staff.fullName}`,
    value: `${staff.staffId}`,
  }));

  const handleSubmit = async () => {
    if (!formData.message) {
      toast.error("Message is required");
      return;
    }

    if (!formData.staffId && !formData.studentId) {
      toast.error("Please select a staff or a student");
      return;
    }

    const payload: MessagePayload = {
      message: formData.message,
      staffId: formData.staffId || null,
      studentId: formData.studentId || null,
      branchCode: formData.branchCode!,
      attachmentUrl: formData.attachmentUrl || "",
    };

    const res = await messagesApis.createMessage(payload);

    if (res) {
      toast.success("Message sent successfully");
      setFormData({
        message: "",
        staffId: 0,
        studentId: 0,
        branchCode,
        attachmentUrl: "",
      });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between">
        <PageHeader showBack />
      </div>

      <div className="bg-white px-10 py-9 rounded-lg flex flex-col gap-16">
        <div className="flex flex-col gap-8">
          <div className="flex max-w-3xl justify-between items-center gap-20">
            <div className="grid lg:grid-cols-2 gap-3 w-full">
              <CustomSelect
                name="staffId"
                label="Select Staff"
                value={formData.staffId || ""}
                options={staffOptions}
                placeholder="Select staff"
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    staffId: Number(e.target.value),
                    studentId: 0, // clear student
                  }));
                }}
              />

              <CustomSelect
                name="studentId"
                label="Select Student"
                value={formData.studentId || ""}
                options={studentOptions}
                placeholder="Select student"
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    studentId: Number(e.target.value),
                    staffId: 0, // clear staff
                  }));
                }}
              />
            </div>

            <div className="flex flex-col gap-2 text-nowrap">
              <DocumentUpload
                onUploadComplete={(url: string) => {
                  setFormData((prev) => ({
                    ...prev,
                    attachmentUrl: url,
                  }));
                }}
                showDropzone={!!formData.attachmentUrl}
                value={formData?.attachmentUrl}
                label="Add Attachment"
                buttonColor="bg-primary-800"
                buttonText="Choose file"
              />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <CustomInput
              name="message"
              label="Message"
              type="text"
              placeholder="Type here...."
              value={formData.message || ""}
              rows={6}
              isTextarea
              onChange={handleInputChange}
              inputClassName="w-full"
              required
            />
            <div className="flex justify-end items-end">
              <CustomButton text="Beautify Text" color="bg-success-600" />
            </div>
          </div>
        </div>
        <div className="flex justify-end items-end gap-4">
          <CustomButton
            text="Schedule for Later"
            border="border-2 border-primary-500"
            textColor="text-primary-500"
            color="bg-white"
            onClick={() => navigate(popupNavigation("scheduleMessage"))}
          />
          <CustomButton
            text="Send Now"
            color="bg-primary-500"
            border="border-2 border-transparent"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}
